import React from 'react';
import axios from 'axios';
import host from '../global';


const DEFAULT_BRANDING = {
  companyName: 'GenieHire',
  logoPrimary: '/genie-demo-logo-black.png',
  logoPrimaryDark: '/genie-demo-logo.png',
  logoCompact: '/Favicon.png',
  // Backward compatibility - old code still uses these
  logoBlack: '/GenieHire-logo-black.png',
  logoWhite: '/GenieHire-logo-black.png',
  logoDemoBlack: '/genie-demo-logo-black.png',
  logoDemo: '/genie-demo-logo.png',
  logoHomepage: '/Homepagelogo.png',
  logoEmail: 'https://nsqrbucket.s3.ap-south-1.amazonaws.com/images/genieHire_email_logo.png',
  contactEmail: 'contact@geniehire.ai',
  platformUrl: 'https://platform.geniehire.ai',
  pdfWatermark: 'genieHire.ai',
  atsSubdomain: null,
  whitelabelEnabled: false
};

let brandingCache = {};


/**
 * Get white-label configuration for any context
 *
 * @param {string} source - 'interview', 'joblink', or 'org'
 * @param {string} id - interview_id, link_token, or org_id
 * @returns {Promise<Object>} Branding configuration
 */

export const getWhiteLabelContext = async (source, id) => {
  if (!source || !id) {
    console.warn('Missing source or id, returning default GenieHire branding');
    return DEFAULT_BRANDING;
  }

  const cacheKey = `${source}:${id}`;

  if (brandingCache[cacheKey]) {
    return brandingCache[cacheKey];
  }

  try {
    const response = await axios.get(`${host}/api/whitelabel-context`, {
      params: { source, id }
    });

    const data = response.data;

    if (!data.whitelabel_enabled) {
      brandingCache[cacheKey] = DEFAULT_BRANDING;
      return DEFAULT_BRANDING;
    }

    const primary = data.logoPrimary;
    const primaryDark = data.logoPrimaryDark;
    const compact = data.logoCompact;

    const branding = {
      companyName: data.companyName || DEFAULT_BRANDING.companyName,
      logoPrimary: primary,
      logoPrimaryDark: primaryDark || primary,
      logoCompact: compact,
      logoBlack: primary,
      logoWhite: primary,
      logoDemoBlack: primary,
      logoDemo: primary,
      logoHomepage: primary,
      logoEmail: primary,
      contactEmail: data.contactEmail || DEFAULT_BRANDING.contactEmail,
      platformUrl: DEFAULT_BRANDING.platformUrl,
      pdfWatermark: data.pdfWatermark || data.companyName || DEFAULT_BRANDING.pdfWatermark,
      atsSubdomain: data.atsSubdomain || null,
      eventLogo: data.eventLogo || null,
      whitelabelEnabled: true
    };

    brandingCache[cacheKey] = branding;
    return branding;

  } catch (error) {
    console.error(`Error fetching white-label context (source=${source}, id=${id}):`, error);
    return DEFAULT_BRANDING;
  }
};

/**
 * @deprecated Use getWhiteLabelContext('org', orgId) instead
 * @param {string} orgId
 * @returns {Promise<Object>} Branding configuration
 */
export const getWhiteLabelConfig = async (orgId) => {
  if (!orgId) {
    console.warn('No orgId provided, returning default GenieHire branding');
    return DEFAULT_BRANDING;
  }

  if (brandingCache[orgId]) {
    return brandingCache[orgId];
  }

  try {
    const response = await axios.get(`${host}/api/whitelabel-config/${orgId}`);
    const data = response.data;

    if (!data.whitelabel_enabled) {
      brandingCache[orgId] = DEFAULT_BRANDING;
      return DEFAULT_BRANDING;
    }

    const primary = data.logoPrimary;
    const primaryDark = data.logoPrimaryDark;
    const compact = data.logoCompact;

    const branding = {
      companyName: data.companyName || DEFAULT_BRANDING.companyName,
      logoPrimary: primary,
      logoPrimaryDark: primaryDark || primary,
      logoCompact: compact,
      logoBlack: primary,
      logoWhite: primary,
      logoDemoBlack: primary,
      logoDemo: primary,
      logoHomepage: primary,
      logoEmail: primary,
      contactEmail: data.contactEmail || DEFAULT_BRANDING.contactEmail,
      platformUrl: DEFAULT_BRANDING.platformUrl,
      pdfWatermark: data.pdfWatermark || data.companyName || DEFAULT_BRANDING.pdfWatermark,
      atsSubdomain: data.atsSubdomain || null,
      whitelabelEnabled: true
    };

    brandingCache[orgId] = branding;
    return branding;

  } catch (error) {
    console.error('Error fetching white-label config:', error);
    return DEFAULT_BRANDING;
  }
};

/**
 * Get branding from interview ID
 * @param {string} interviewId
 * @returns {Promise<Object>} Branding configuration
 */
export const getWhiteLabelFromInterview = async (interviewId) => {
  return getWhiteLabelContext('interview', interviewId);
};

/**
 * Get branding from job link token
 * @param {string} linkToken
 * @returns {Promise<Object>} Branding configuration
 */
export const getWhiteLabelFromJobLink = async (linkToken) => {
  return getWhiteLabelContext('joblink', linkToken);
};

/**
 * Clear branding cache
 * @param {string|null} orgId - Org ID to clear, or null for all
 */
export const clearBrandingCache = (orgId = null) => {
  if (orgId) {
    delete brandingCache[orgId];
    console.log(`Cleared branding cache for org: ${orgId}`);
  } else {
    brandingCache = {};
    console.log('Cleared all branding cache');
  }
};


/**
 * React hook for white-label configuration
 * @param {string} orgId
 * @returns {Object} { branding, loading }
 */
export const useWhiteLabel = (orgId) => {
  const [branding, setBranding] = React.useState(DEFAULT_BRANDING);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!orgId) {
      setBranding(DEFAULT_BRANDING);
      setLoading(false);
      return;
    }

    setLoading(true);
    getWhiteLabelConfig(orgId)
      .then(config => {
        setBranding(config);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error in useWhiteLabel:', error);
        setBranding(DEFAULT_BRANDING);
        setLoading(false);
      });
  }, [orgId]);

  return { branding, loading };
};


export default {
  getWhiteLabelContext,
  getWhiteLabelConfig,
  getWhiteLabelFromInterview,
  getWhiteLabelFromJobLink,
  clearBrandingCache,
  useWhiteLabel,
  DEFAULT_BRANDING
};
